import { Scope } from '@developerz/domain';
import type { JSX } from 'solid-js';
import { SystemPromptsView } from '../components/SystemPromptsView';
import { useScopeVerdict } from '../lib/scope-gate';
import { SESSION_TOKEN } from '../lib/session';
import {
  clearMyPrompt,
  clearOrgPrompt,
  setMyPrompt,
  setOrgPrompt,
  useMyPrompt,
  useOrgPrompt,
} from '../lib/system-prompts';
import { usePromptLayer } from '../lib/use-prompt-editor';

// System-prompt cascade route (epic #635, #637). CONTAINER: wires the ORG
// (account-wide) and USER (self) layers to their editors; the REPO layer lives
// on the repo settings tab. Prose layers SPECIALIZE the built-in agent prompt,
// never replace it — order platform → org → repo → user. JSX → SystemPromptsView.
export function SystemPrompts(props: { token?: string | null } = {}): JSX.Element {
  // `=== undefined`, never `??`: an explicit `null` MEANS signed out and must
  // survive. A prod build's `SESSION_TOKEN` is the non-null `__cookie_session__`
  // marker, so `??` would swallow that null, mount both prompt queries and leave
  // the view's no-session branch unreachable anywhere but a test.
  const token = props.token === undefined ? SESSION_TOKEN : props.token;

  // The ORG layer's routes are all `write:prompts`, GET included; a member holds
  // only `write:self:prompt`, so their org read waits on `/v1/me` and never
  // fires when it lacks the scope (#7426). The USER layer is every member's.
  const orgVerdict = useScopeVerdict(Scope.WritePrompts, token);
  const org = usePromptLayer({
    read: (role) => useOrgPrompt(role, token, () => orgVerdict() === 'allowed'),
    save: (text, role) => setOrgPrompt(text, role, token),
    clear: (role) => clearOrgPrompt(role, token),
  });
  const mine = usePromptLayer({
    read: (role) => useMyPrompt(role, token),
    save: (text, role) => setMyPrompt(text, role, token),
    clear: (role) => clearMyPrompt(role, token),
  });

  return (
    <SystemPromptsView
      hasSession={token !== null}
      token={token}
      orgVerdict={orgVerdict()}
      orgQuery={org.query}
      org={org.editor}
      mineQuery={mine.query}
      mine={mine.editor}
    />
  );
}
